import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { Permissions, defaultPermissions } from '../permissions';

interface User {
  id: string;
  username: string;
  name: string;
  nameAr: string;
  role: string;
}

interface UserContextType {
  user: User;
  setUser: (user: User) => void;
  userName: string;
  isLoggedIn: boolean;
  permissions: Permissions | null;
  login: (userData: any) => void;
  logout: () => void;
}

const defaultUser: User = {
  id: '',
  username: '',
  name: '',
  nameAr: '',
  role: '',
};

const UserContext = createContext<UserContextType>({
  user: defaultUser,
  setUser: () => {},
  userName: '',
  isLoggedIn: false,
  permissions: null,
  login: () => {},
  logout: () => {},
});

export function UserProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User>(defaultUser);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [permissions, setPermissions] = useState<Permissions | null>(null);

  // Restore session from localStorage
  useEffect(() => {
    const savedUser = localStorage.getItem('manara_user');
    const savedPerms = localStorage.getItem('manara_permissions');
    if (savedUser) {
      try {
        const parsed = JSON.parse(savedUser);
        setUser(parsed);
        setIsLoggedIn(true);
        if (savedPerms) {
          setPermissions(JSON.parse(savedPerms));
        } else {
          setPermissions(defaultPermissions[parsed.role] || defaultPermissions.viewer);
        }
      } catch {}
    }
  }, []);

  const userName = user.name;

  const login = (userData: any) => {
    const { permissions: perms, token, ...userInfo } = userData;
    const userPerms = perms || defaultPermissions[userInfo.role] || defaultPermissions.viewer;
    setUser(userInfo);
    setPermissions(userPerms);
    setIsLoggedIn(true);
    if (token) localStorage.setItem('manara_token', token);
    localStorage.setItem('manara_user', JSON.stringify(userInfo));
    localStorage.setItem('manara_permissions', JSON.stringify(userPerms));
  };

  const logout = () => {
    setUser(defaultUser);
    setPermissions(null);
    setIsLoggedIn(false);
    localStorage.removeItem('manara_token');
    localStorage.removeItem('manara_user');
    localStorage.removeItem('manara_permissions');
  };

  return (
    <UserContext.Provider value={{ user, setUser, userName, isLoggedIn, permissions, login, logout }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  return useContext(UserContext);
}
